import {Record as ImmRecord, type RecordOf} from 'immutable';
import type {Event} from '@packages/utilities/analytics';
import type {DesignerAbilitySelectorName} from '@packages/systems/designer-abilities';
import * as React from 'react';
import type {DesignerEventName} from '@packages/shared/segment-typewriter';

type Action = {
  type: string;
  payload?: Record<string, any>;
};

type CTAActionCreatorType = {
  descriptor: [string, string];
  args?: unknown[];
};

type NotificationType =
  | 'info'
  | 'ban'
  | 'error'
  | 'warning'
  | 'success'
  | 'love'
  | 'loading'
  | 'import'
  | 'university'
  | 'webflow';

export type NotificationMessage =
  | string
  | Array<{
      text: React.ReactNode;
      style?: Record<string, any>;
      action?: Action;
    }>;

export type NotificationShape = {
  id: string | null;
  permanentlyDismissable: boolean;
  dismissableUntilReload: boolean;
  type: NotificationType;
  message: NotificationMessage;
  timestamp: number;
  title: string;
  delay: number;
  icon: boolean | React.ReactElement;
  dismiss: string;
  dismissIcon: boolean;
  action: Action | null;
  ctaAction: Action | null;
  ctaActionCreator?: null | CTAActionCreatorType;
  callToActionText: string | null;
  callToActionTextAsButton: boolean;
  callToActionIcon: string | null;
  ctaDismiss: boolean;
  customContainer?: boolean;
  ctaActionWithDismiss: boolean;
  borderless: boolean;
  ctaTrackEvent: null | Event<DesignerEventName>;
  dismissTrackEvent: null | Event<DesignerEventName>;
  designerAbility: DesignerAbilitySelectorName | null;
  customButtons: ((props: {
    dismiss: () => void;
    dispatch: (action: any) => void;
  }) => React.ReactNode)[];
};

export type Notification = RecordOf<NotificationShape>;

export default ImmRecord<NotificationShape>({
  /**
   * (Optional)
   * A unique identifier for this type of notification.
   * Useful for notifications that need to be permanently dismissed.
   * @type {String}
   */
  id: null,

  /**
   * Whether future notifications with a matching ID will be suppressed after their first dismissal
   * @type {Boolean}
   */
  permanentlyDismissable: false,

  /**
   * Whether future notifications with a matching ID will be suppressed after their first dismissal
   * until the next designer reload. The dismissal is kept in memory only and is
   * never written to persistedUIState, so the notification can re-surface after a reload.
   * @type {Boolean}
   */
  dismissableUntilReload: false,

  /**
   * One of 'info', 'ban', 'error', 'warning', 'success', 'love', 'loading', 'import', 'university', 'webflow'.
   * @type {String}
   */
  type: 'warning',

  /**
   * Main notification text.
   * @type {String}
   */
  message: '',

  /**
   * Timestamp in milliseconds of when notification was triggered.
   * @type {Number}
   */
  timestamp: 0,

  /**
   * (Optional) Notification heading
   * @type {String}
   */
  title: '',

  /**
   * (Optional) Time in milliseconds after which to hide the notification.
   * @type {Number}
   */
  delay: 0,

  /**
   * (Optional) Should an icon be displayed next to the messsage?
   * @type {String}
   */
  icon: false,

  /**
   * (Optional) Label for button that will appear to allow notifications to be
   * dismissed manually.
   * @type {String}
   */
  dismiss: '',

  /**
   * (Optional) Flag indicating if notification will have a X close button
   * to dismiss manually. `dismiss` has priority over dismissIcon
   * @type {Boolean}
   */
  dismissIcon: false,

  /**
   * (Optional) Flux action to be triggered when clicking the dismiss button,
   * in addition to dismissing the notification.
   * @type {Object}
   */
  action: null,

  /**
   * (Optional) Flux action to be triggered when clicking the CTA button.
   * @type {Object}
   */
  ctaAction: null,

  /**
   * (Optional) Flux action creator to be triggered when clicking the CTA button.
   * @type {Object}
   */
  ctaActionCreator: null,

  /**
   * (Optional) Flux action to be triggered when clicking the button,
   * in addition to dismissing the notification.
   * @type {String}
   */
  callToActionText: null,

  /**
   * (Optional) Flag indicating if CTA should be styled with Button component
   * @type {Boolean}
   */
  callToActionTextAsButton: false,

  /**
   * (Optional) Icon string that will map to an Icon to render in CTA button.
   * @type {String}
   */
  callToActionIcon: null,

  /**
   * (Optional) Flag indicating if notification should be dismissed when
   * `callToActionText` is clicked. This will have priority over `ctaAction`
   * @type {Boolean}
   */
  ctaDismiss: false,

  /**
   * (Optional) A flag telling if the notification is to be rendered by custom logic
   * and not by NotificationList component.
   * @type {Boolean}
   */
  customContainer: false,

  /**
   * (Optional) Couples the CTA action to a dismiss without overriding the ctaAction
   * or the dismiss.
   * @type {Boolean}
   */
  ctaActionWithDismiss: false,

  /**
   * (Optional) Updates the Notification styling to remove left border
   * @type {Boolean}
   */
  borderless: false,

  /**
   * (Optional) Analytics event to be triggered when clicking the CTA button.
   * @type {Object}
   */
  ctaTrackEvent: null,

  /**
   * (Optional) Analytics event to be triggered on dismissing notification.
   * @type {Object}
   */
  dismissTrackEvent: null,

  /**
   * (Optional) The designer ability needed to display the notification.
   * @type {String | null}
   */
  designerAbility: null,

  /**
   * (Optional) Custom buttons to be rendered in the notification.
   * @type {Array}
   */
  customButtons: [],
});
